import React from 'react';
import {graphql, useFragment} from 'react-relay';
import {useIntl} from 'react-intl';
import Styled from 'styled-components/macro';
import {
	Announcements,
	closestCenter,
	DndContext,
	DragEndEvent,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import {SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import {Button, EMPHASIS, Icon, Tooltip} from '@forecast-it/design-system';
import SettingsProjectSubNav from './SettingsProjectSubNav';
import {canDeleteStage, stageLabel} from '../../shared/util/projectStageUtil';
import {ForecastThemeProvider} from '../../shared/ForecastThemeProvider';
import {useForecastFetchQuery} from '../../shared/hooks/useForecastFetchQuery';
import {showProjectStageModal} from './modals/ProjectStageModal';
import {showDeleteProjectStageModal} from './modals/DeleteProjectStageModal';
import {useProjectStageOrder} from './useProjectStageOrder';
import type {SettingsProjectStages_viewer$key} from './__generated__/SettingsProjectStages_viewer.graphql';
import type {SettingsProjectStagesQuery$data} from './__generated__/SettingsProjectStagesQuery.graphql';

const Page = Styled.div`
	max-width: 1000px;
	margin-left: 24px;
	margin-top: 6px;
	font-size: 13px;
`;

const StageList = Styled.ol`
	list-style: none;
	padding: 0;
	margin: 0 0 12px;
	max-width: 620px;
`;

const StageRow = Styled.li`
	display: flex;
	align-items: center;
	justify-content: space-between;
	min-height: 40px;
	padding: 0 12px;
	border-bottom: 1px solid #e6e6e6;
`;

const StageLabel = Styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`;

const RowActions = Styled.div`
	display: flex;
	align-items: center;
`;

const DragHandle = Styled.button`
	display: flex;
	align-items: center;
	padding: 4px;
	border: none;
	background: none;
	cursor: grab;
	&[aria-disabled='true'] {
		cursor: default;
		opacity: 0.4;
	}
`;

// Names the icon-only edit button: the design system's Button overwrites aria-label, so the name comes
// from this visually hidden element through aria-labelledby.
const AssistiveText = Styled.span`
	position: absolute;
	width: 1px;
	height: 1px;
	margin: -1px;
	padding: 0;
	border: 0;
	overflow: hidden;
	white-space: nowrap;
	clip: rect(0 0 0 0);
	clip-path: inset(50%);
`;

export type ProjectStageRow = {
	id: string;
	name: string | null;
	profile?: string | null;
	sortOrder: number | null;
	projectCount?: number | null;
};

const SortableStageRow = ({
	id,
	label,
	handleLabel,
	disabled,
	children,
}: {
	id: string;
	label: string;
	handleLabel: string;
	disabled: boolean;
	children?: React.ReactNode;
}): JSX.Element => {
	const {attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging} = useSortable({
		id,
		disabled,
	});
	return (
		<StageRow
			ref={setNodeRef}
			style={{transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1}}
			data-cy={'project-stage-row'}
		>
			<StageLabel>
				<DragHandle
					type="button"
					ref={setActivatorNodeRef}
					{...attributes}
					{...listeners}
					aria-label={handleLabel}
					// aria-disabled rather than disabled: a natively disabled handle drops keyboard focus
					// to <body> after a drop. useSortable({disabled}) already stops drags while saving.
					aria-disabled={disabled}
					data-cy={'project-stage-drag-handle'}
				>
					<Icon icon={'drag'} size={'m'} />
				</DragHandle>
				<span data-cy={'project-stage-name'}>{label}</span>
			</StageLabel>
			{children}
		</StageRow>
	);
};

export const SettingsProjectStagesComponent = ({
	companyId,
	stages,
	refetchStages = () => Promise.resolve(),
}: {
	companyId: string;
	stages: ReadonlyArray<ProjectStageRow>;
	refetchStages?: () => Promise<unknown>;
}): JSX.Element => {
	const intl = useIntl();
	const {ordered, saving, moveStage} = useProjectStageOrder(stages, refetchStages);
	const editLabel = intl.formatMessage({id: 'settings.project_stages.edit'});
	const deleteLabel = intl.formatMessage({id: 'settings.project_stages.delete'});

	// Default stage names are translated when a translation exists; any other name is shown as stored.
	const label = (stage: ProjectStageRow) => stageLabel(stage, intl);

	// The duplicate check runs against stored names, so the defaults are compared by their English names
	// exactly as the server compares them.
	const namesExcept = (excludedId?: string) =>
		ordered.filter(stage => stage.id !== excludedId).map(stage => stage.name ?? '');

	const handleAdd = () => showProjectStageModal({companyId, otherNames: namesExcept()});

	const handleEdit = (stage: ProjectStageRow) =>
		showProjectStageModal({
			companyId,
			stage: {id: stage.id, name: stage.name ?? ''},
			otherNames: namesExcept(stage.id),
		});

	const handleDelete = (stage: ProjectStageRow) =>
		showDeleteProjectStageModal({
			companyId,
			stage: {id: stage.id, label: label(stage)},
			projectCount: stage.projectCount ?? 0,
		});

	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {coordinateGetter: sortableKeyboardCoordinates})
	);

	const labelOf = (id: string | number) => {
		const stage = ordered.find(candidate => candidate.id === id);
		return stage ? label(stage) : '';
	};
	const positionOf = (id: string | number) => ordered.findIndex(candidate => candidate.id === id) + 1;

	const announcements: Announcements = {
		onDragStart: ({active}) =>
			intl.formatMessage({id: 'settings.project_stages.drag.picked_up'}, {name: labelOf(active.id)}),
		onDragOver: ({active, over}) =>
			over
				? intl.formatMessage(
						{id: 'settings.project_stages.drag.moved'},
						{name: labelOf(active.id), position: positionOf(over.id), total: ordered.length}
				  )
				: undefined,
		onDragEnd: ({active, over}) =>
			over
				? intl.formatMessage(
						{id: 'settings.project_stages.drag.dropped'},
						{name: labelOf(active.id), position: positionOf(over.id), total: ordered.length}
				  )
				: undefined,
		onDragCancel: ({active}) =>
			intl.formatMessage({id: 'settings.project_stages.drag.cancelled'}, {name: labelOf(active.id)}),
	};

	const handleDragEnd = ({active, over}: DragEndEvent) => {
		if (over) {
			moveStage(String(active.id), String(over.id));
		}
	};

	return (
		<div className="section-content settings-app" data-cy={'settings-project-stages-page'}>
			<ForecastThemeProvider>
				<>
					<SettingsProjectSubNav />
					<Page>
						<DndContext
							sensors={sensors}
							collisionDetection={closestCenter}
							onDragEnd={handleDragEnd}
							accessibility={{
								announcements,
								screenReaderInstructions: {
									draggable: intl.formatMessage({id: 'settings.project_stages.drag.instructions'}),
								},
							}}
						>
							<SortableContext items={ordered.map(stage => stage.id)} strategy={verticalListSortingStrategy}>
								<StageList data-cy={'project-stages-list'}>
									{ordered.map(stage => {
										const editLabelId = `project-stage-edit-label-${stage.id}`;
										const deleteLabelId = `project-stage-delete-label-${stage.id}`;
										return (
											<SortableStageRow
												key={stage.id}
												id={stage.id}
												label={label(stage)}
												handleLabel={intl.formatMessage(
													{id: 'settings.project_stages.reorder_handle'},
													{name: label(stage)}
												)}
												disabled={saving}
											>
												<RowActions>
													<AssistiveText id={editLabelId}>{editLabel}</AssistiveText>
													<Tooltip content={editLabel} interactable={false}>
														<Button
															emphasis={EMPHASIS.LOW}
															icon={'edit'}
															iconOnly
															onPress={() => handleEdit(stage)}
															aria-labelledby={editLabelId}
															data-cy={'project-stage-edit'}
														/>
													</Tooltip>
													{canDeleteStage(stage) && (
														<>
															<AssistiveText id={deleteLabelId}>{deleteLabel}</AssistiveText>
															<Tooltip content={deleteLabel} interactable={false}>
																<Button
																	emphasis={EMPHASIS.LOW}
																	icon={'trash'}
																	iconOnly
																	onPress={() => handleDelete(stage)}
																	aria-labelledby={deleteLabelId}
																	data-cy={'project-stage-delete'}
																/>
															</Tooltip>
														</>
													)}
												</RowActions>
											</SortableStageRow>
										);
									})}
								</StageList>
							</SortableContext>
						</DndContext>
						<Button emphasis={EMPHASIS.LOW} icon={'plus'} onPress={handleAdd} data-cy={'project-stages-add'}>
							{intl.formatMessage({id: 'settings.project_stages.add'})}
						</Button>
					</Page>
				</>
			</ForecastThemeProvider>
		</div>
	);
};

export const SettingsProjectStagesQuery = graphql`
	query SettingsProjectStagesQuery {
		viewer {
			actualPersonId
			component(name: "settings_project_stages")
			...SettingsProjectStages_viewer
		}
	}
`;

const VIEWER_FRAGMENT = graphql`
	fragment SettingsProjectStages_viewer on Viewer {
		company {
			id
			projectStages(first: 1000) @connection(key: "Company_projectStages") {
				edges {
					node {
						id
						name
						sortOrder
						profile
						projectCount
					}
				}
			}
		}
	}
`;

const SettingsProjectStages = ({viewer}: {viewer: SettingsProjectStages_viewer$key}): JSX.Element => {
	const data = useFragment(VIEWER_FRAGMENT, viewer);
	const {fetch} = useForecastFetchQuery<SettingsProjectStagesQuery$data, Record<string, never>>(SettingsProjectStagesQuery);
	const refetchStages = () => new Promise((resolve, reject) => fetch({}, resolve, reject));
	const stages = (data.company?.projectStages?.edges ?? []).flatMap(edge => (edge?.node ? [edge.node] : []));
	return <SettingsProjectStagesComponent companyId={data.company?.id ?? ''} stages={stages} refetchStages={refetchStages} />;
};

export default SettingsProjectStages;
